import {v4 as uuid} from 'uuid'
import {CompanyContactsFormData} from '@/pages/onboarding/CompanyContacts/types'
import {
    BusinessStructure,
    CompanyDetailsFormData,
    CompanyInfoCorporateEntityShareholder,
    CompanyInfoDirector,
    CompanyInfoIndividualShareholder,
    CompanyInfoResponse,
    CorporateOwnerData,
    DirectorData,
    OwnerData,
    SaveApplicationRequest,
    StartApplication
} from '@/stores/onboarding'
import {AuthStore} from '@/stores/auth/AuthStore'

export const mapEntityTypeToBusinessStructure = (entityType: string): BusinessStructure => {
    const mapping: Record<string, BusinessStructure> = {
        company: BusinessStructure.COMPANY,
        ['sole-trader']: BusinessStructure.SOLE_TRADER,
        charity: BusinessStructure.CHARITY,
        partnership: BusinessStructure.PARTNERSHIP,
        llp: BusinessStructure.LLP,
        other: BusinessStructure.OTHER
    }

    return mapping[entityType] || BusinessStructure.COMPANY
}

export const mapCompanyDetailsToStartApplication = (
    companyDetails: CompanyDetailsFormData,
    userEmail: string
): StartApplication => {
    return {
        applicantInfo: {
            initiatorEmail: userEmail
        },
        companyProfile: {
            companyName: companyDetails.companyName || '',
            companyTradeName: companyDetails.companyTradeName || '',
            companyNumber: companyDetails.companyNumber || '',
            numberOfEmployees: companyDetails.numberOfEmployees || '',
            dateOfCreation: companyDetails.dateOfCreation || '',
            ...(companyDetails.entityType === BusinessStructure.CHARITY && {
                charityNumber: companyDetails.charityNumber || null,
                charityCommission: companyDetails.charityCommission || null
            })
        },
        businessStructure: mapEntityTypeToBusinessStructure(companyDetails.entityType)
    }
}

export const mapFormDataToSaveApplication = (
    companyDetails?: CompanyDetailsFormData,
    companyContacts?: CompanyContactsFormData,
    existingData?: any
): SaveApplicationRequest => {
    const result: SaveApplicationRequest = {
        ...existingData,
        version: '2.1'
    }

    if (companyDetails) {
        result.businessStructure = mapEntityTypeToBusinessStructure(companyDetails.entityType)

        result.companyProfile = {
            ...result.companyProfile,
            companyName: companyDetails.companyName || '',
            companyTradeName: companyDetails.companyTradeName || '',
            companyNumber: companyDetails.companyNumber || '',
            dateOfCreation: companyDetails.dateOfCreation || '',
            numberOfEmployees: companyDetails.numberOfEmployees || ''
        }

        if (companyDetails.entityType === 'other' && companyDetails.customEntityType) {
            result.companyProfile.otherDescription = companyDetails.customEntityType
        }

        if (companyDetails.entityType === BusinessStructure.CHARITY) {
            result.companyProfile.charityNumber = companyDetails.charityNumber
                ? companyDetails.charityNumber
                : null
            result.companyProfile.charityCommission = companyDetails.charityCommission
                ? companyDetails.charityCommission
                : null
        }
    }

    if (companyContacts) {
        result.companyProfile = {
            ...result.companyProfile,
            emailAddress: companyContacts.emailAddress || '',
            telephoneNumber: companyContacts.telephoneNumber || '',
            companyWebsite: companyContacts.companyWebsite || '',
            companyAddress: {
                country: 'United Kingdom',
                postalCode: companyContacts.address?.postalCode || '',
                addressLine1: companyContacts.address?.addressLine1 || '',
                addressLine2: companyContacts.address?.addressLine2 || '',
                townOrCity: companyContacts.address?.townOrCity || '',
                countyOrState: companyContacts.address?.county || ''
            }
        }
    }

    return result
}

export const getCurrentUserEmail = (authStore: AuthStore): string => {
    return authStore.email
}

export const mapCompanyDirectorToFormData = (
    director: CompanyInfoDirector,
    index: number
): DirectorData => {
    return {
        id: director.id || uuid(),
        title: director.title || '',
        firstName: director.firstName || '',
        lastName: director.surname || '',
        surname: director.surname || '',
        emailAddress: director.emailAddress || '',
        phone: director.telephoneNumber || '',
        telephoneNumber: director.telephoneNumber || '',
        dateOfBirth: director.dateOfBirth,
        nationality: director.nationality || '',
        placeOfBirth: director.placeOfBirth || '',
        gender: director.gender || '',
        position: director.occupation || '',
        agreementSignatory: index === 0,
        isComplete: director.isVerified === true,
        uploadDocManually: false,
        isVerified: director.isVerified || false,
        deletionAllowed: director.deletionAllowed !== false,
        preFilledFromApi: {
            title: false,
            firstName: !!(director.firstName && director.firstName.trim()),
            middleName: !!(director.middleName && director.middleName.trim()),
            surname: !!(director.surname && director.surname.trim()),
            dateOfBirth: !!director.dateOfBirth,
            nationality: !!(director.nationality && director.nationality.trim()),
            placeOfBirth: !!(director.placeOfBirth && director.placeOfBirth.trim()),
            gender: false,
            emailAddress: !!(director.emailAddress && director.emailAddress.trim()),
            telephoneNumber: !!(director.telephoneNumber && director.telephoneNumber.trim())
        },
        address: {
            addressLine1: '',
            addressLine2: '',
            townOrCity: '',
            postalCode: '',
            country: 'United Kingdom'
        }
    }
}

export const mapIndividualShareholderToFormData = (
    shareholder: CompanyInfoIndividualShareholder,
    index: number
): OwnerData => {
    return {
        id: shareholder.id || uuid(),
        title: shareholder.title || '',
        firstName: shareholder.firstName || '',
        lastName: shareholder.surname || '',
        surname: shareholder.surname || '',
        emailAddress: shareholder.emailAddress || '',
        phone: shareholder.telephoneNumber || '',
        telephoneNumber: shareholder.telephoneNumber || '',
        dateOfBirth: shareholder.dateOfBirth,
        nationality: shareholder.nationality || '',
        ownershipPercentage: shareholder.ownershipPercentage,
        isComplete: shareholder.isVerified === true,
        uploadDocManually: false,
        isVerified: shareholder.isVerified || false,
        isCompany: false,
        deletionAllowed: shareholder.deletionAllowed !== false,
        preFilledFromApi: {
            title: false,
            firstName: !!(shareholder.firstName && shareholder.firstName.trim()),
            middleName: !!(shareholder.middleName && shareholder.middleName.trim()),
            ownershipPercentage: !!shareholder.ownershipPercentage,
            surname: !!(shareholder.surname && shareholder.surname.trim()),
            dateOfBirth: !!shareholder.dateOfBirth,
            nationality: !!(shareholder.nationality && shareholder.nationality.trim()),
            emailAddress: !!(shareholder.emailAddress && shareholder.emailAddress.trim()),
            telephoneNumber: !!(shareholder.telephoneNumber && shareholder.telephoneNumber.trim())
        },
        address: {
            addressLine1: '',
            townOrCity: '',
            postalCode: '',
            country: 'United Kingdom'
        }
    }
}

export const mapCorporateShareholderToFormData = (
    shareholder: CompanyInfoCorporateEntityShareholder,
    index: number
): CorporateOwnerData => {
    return {
        id: shareholder.id || uuid(),
        companyName: shareholder.companyName || '',
        companyNumber: shareholder.companyNumber || '',
        ownershipPercentage: shareholder.ownershipPercentage || 0,
        isComplete: true,
        isVerified: shareholder.isVerified || false,
        uploadDocManually: shareholder.uploadDocManually || false,
        deletionAllowed: shareholder.deletionAllowed !== false,
        fromApi: shareholder.fromApi !== false,
        officerVerificationResult: shareholder.officerVerificationResult,
        agreementSignatory: shareholder.agreementSignatory,
        jumioCheckResults: shareholder.jumioCheckResults,
        jumioRequestSent: shareholder.jumioRequestSent,
        preFilledFromApi: {
            companyName: !!(shareholder.companyName && shareholder.companyName.trim()),
            companyNumber: !!(shareholder.companyNumber && shareholder.companyNumber.trim()),
            ownershipPercentage:
                shareholder.ownershipPercentage !== undefined &&
                shareholder.ownershipPercentage !== null
        }
    }
}

export const mapCompanyInfoToOwnersAndDirectors = (
    companyInfo: CompanyInfoResponse
): {
    directors: DirectorData[]
    owners: OwnerData[]
    corporateOwners: CorporateOwnerData[]
} => {
    const directors =
        companyInfo.companyDirectors?.map((director, index) =>
            mapCompanyDirectorToFormData(director, index)
        ) || []

    const owners =
        companyInfo.individualShareholders?.map((shareholder, index) =>
            mapIndividualShareholderToFormData(shareholder, index)
        ) || []

    const corporateOwners =
        companyInfo.corporateEntityShareholders?.map((shareholder, index) =>
            mapCorporateShareholderToFormData(shareholder, index)
        ) || []

    return {directors, owners, corporateOwners}
}
